import React, { useState } from 'react'

import DOMPurify from 'dompurify'

import validation from '../../../validation'
import AddButton from '../../controls/AddButton'
import CheckBox from '../../controls/CheckBox'
import RemoveButton from '../../controls/RemoveButton'
import TextArea from '../../controls/TextArea'
import HousingSelector from '../../HousingSelector'

export default {
  name: 'Home Setup',
  group: 'Case Details',
  validationSchema: getValidationSchema(),
  Component,
}

function getValidationSchema() {
  return validation.object({
    homeSetup: validation
      .arrayOf(
        validation.object({
          type: validation.name('Type of Housing').required(),
          inaccessibilities: validation.richText('Inaccessibilities'),
        }),
      )
      .nullable(),
  })
}

function Component({ addItemToArray, removeItemFromArray, createInputProps, getValue, ErrorMessage }) {
  const [editingIndex, setEditingIndex] = useState(null)
  const homeSetups = getValue('homeSetup', [])

  const handleAddHomeSetup = () => {
    const nextIndex = homeSetups.length
    addItemToArray('homeSetup', {})
    setEditingIndex(nextIndex)
  }

  const handleRemove = index => {
    removeItemFromArray('homeSetup', index)
    if (editingIndex === index) {
      setEditingIndex(null)
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1)
    }
  }

  return (
    <div>
      <h4 className="pb-3">Home Setup</h4>
      {homeSetups.map((x, index) => {
        const namePrefix = `homeSetup[${index}]`
        const isEditing = editingIndex === index
        return (
          <React.Fragment key={namePrefix}>
            <HomeSetup
              namePrefix={namePrefix}
              createInputProps={createInputProps}
              getValue={getValue}
              ErrorMessage={ErrorMessage}
              isEditing={isEditing}
              onEdit={() => setEditingIndex(index)}
              onDone={() => setEditingIndex(null)}
              handleRemove={() => handleRemove(index)}
            />
            <hr />
          </React.Fragment>
        )
      })}
      <AddButton id="TransportationAddButton" onClick={handleAddHomeSetup}>
        Add Home Setup
      </AddButton>
    </div>
  )
}

function HomeSetup({ namePrefix, createInputProps, ErrorMessage, handleRemove, getValue, isEditing, onEdit, onDone }) {
  const type = getValue(`${namePrefix}.type`, '')
  const inaccessibilities = getValue(`${namePrefix}.inaccessibilities`, '')
  const wheelchairAccessible = getValue(`${namePrefix}.wheelchairAccessible`)
  const hasInaccessibilities = Boolean(inaccessibilities && inaccessibilities.replace(/<[^>]*>/g, '').trim())

  if (!isEditing) {
    return (
      <div className="d-flex align-items-start justify-content-between clearfix">
        <div className="w-100 mr-3">
          <div>
            <span className="font-weight-semibold">{type || <em className="text-muted">Untitled Housing</em>}</span>
            {wheelchairAccessible && <span className="badge badge-success ml-3">Wheelchair Accessible</span>}
          </div>
          {hasInaccessibilities && (
            <div className="text-muted mt-1" style={{ fontSize: '0.85em' }}>
              <span>Inaccessibilities: </span>
              <div className="d-inline" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(inaccessibilities) }} />
            </div>
          )}
        </div>
        <div className="d-flex align-items-center" style={{ gap: '8px', flexShrink: 0 }}>
          <button type="button" className="btn btn-link btn-sm p-0" onClick={onEdit} title="Edit this home setup">
            <i className="fas fa-pen" aria-hidden="true" />
          </button>
          <RemoveButton id={`remove-${namePrefix}`} className="btn-sm" onClick={handleRemove}>
            Remove
          </RemoveButton>
        </div>
      </div>
    )
  }

  return (
    <div className="clearfix">
      <div className="form-row">
        <div className="form-group col-md-6">
          <label className="form-label" htmlFor={`${namePrefix}.type`}>
            Type of Housing
          </label>
          <HousingSelector id={`${namePrefix}.type`} {...createInputProps(`${namePrefix}.type`)} />
          <ErrorMessage name={`${namePrefix}.type`} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group col-md-8">
          <label className="form-label" htmlFor={`${namePrefix}.inaccessibilities`}>
            Inaccessibilities
          </label>
          <TextArea
            areaProps={{ ...createInputProps(`${namePrefix}.inaccessibilities`) }}
            rows={3}
            maxLength={validation.maxLengths.richText}
          />
          <ErrorMessage name={`${namePrefix}.inaccessibilities`} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group mt-1 col-6 col-sm-6 col-md-3 col-lg-3">
          <CheckBox {...createInputProps(`${namePrefix}.wheelchairAccessible`)}>Wheelchair Accessible</CheckBox>
        </div>
      </div>
      <div className="form-row mb-2 mt-2">
        <div className="col-12 d-flex align-items-end justify-content-end pb-1" style={{ gap: '8px' }}>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onDone}>
            Done
          </button>
          <RemoveButton id={`remove-${namePrefix}`} className="btn-sm" onClick={handleRemove}>
            Remove
          </RemoveButton>
        </div>
      </div>
    </div>
  )
}
